import { useState } from "react";
import { styled, Switch } from "@mui/material";
import { FieldCard } from "@components/form/FieldCard";
import { FieldInput, FieldTextarea } from "@components/form/FieldInput";
import { PickerSheet, PickerOption } from "@components/form/PickerSheet";
import { tokens } from "@styles/tokens";
import { ICustomFieldDefinition } from "@Clients/CustomFieldsClient";
import type { CustomFieldValue } from "../../../hooks/useProductForm";

interface CustomFieldProps {
  column: ICustomFieldDefinition;
  value: CustomFieldValue;
  error?: string;
  onChange: (value: CustomFieldValue) => void;
}

/**
 * Renders one org-defined custom field on the forms pattern, picking the right
 * control for its type: text/number/date inputs, a multiline textarea, a
 * boolean toggle, or a select that opens a picker sheet.
 */
export const CustomField = ({
  column,
  value,
  error,
  onChange,
}: CustomFieldProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const current = value ?? column.default_value ?? "";

  if (column.type === "textarea") {
    return (
      <FieldCard
        label={column.label}
        align="start"
        helper={column.help_text ?? undefined}
        error={error}
      >
        <FieldTextarea
          value={String(current)}
          placeholder={column.placeholder || ""}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
        />
      </FieldCard>
    );
  }

  if (column.type === "boolean") {
    return (
      <FieldCard
        label={column.label}
        helper={column.help_text ?? undefined}
        trailing={
          <Switch
            size="small"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
      >
        <BoolText>{value ? "Yes" : "No"}</BoolText>
      </FieldCard>
    );
  }

  if (column.type === "select") {
    const options: PickerOption[] = (column.field_options || []).map((o) => ({
      value: o.value,
      label: o.label,
    }));
    const selected = options.find((o) => o.value === current);
    return (
      <>
        <FieldCard
          label={column.label}
          onClick={() => setPickerOpen(true)}
          helper={column.help_text ?? undefined}
          error={error}
        >
          {selected ? (
            <span>{selected.label}</span>
          ) : (
            <Placeholder>
              {column.placeholder || `Select ${column.label}`}
            </Placeholder>
          )}
        </FieldCard>
        <PickerSheet
          open={pickerOpen}
          title={column.label}
          options={options}
          selectedValue={String(current)}
          onSelect={(v) => onChange(v)}
          onClose={() => setPickerOpen(false)}
        />
      </>
    );
  }

  const type =
    column.type === "number" ? "number" : column.type === "date" ? "date" : "text";
  return (
    <FieldCard label={column.label} helper={column.help_text ?? undefined} error={error}>
      <FieldInput
        type={type}
        value={current as string | number}
        placeholder={column.placeholder || ""}
        onChange={(e) =>
          onChange(
            column.type === "number"
              ? e.target.value === ""
                ? null
                : Number(e.target.value)
              : e.target.value
          )
        }
      />
    </FieldCard>
  );
};

const BoolText = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.body,
}));

const Placeholder = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.subtle,
}));
